/**
 * Table Export Utility
 * 
 * Provides functions to export table data to PDF and Excel (CSV) formats
 * For government project - reliable and secure export functionality
 */

import JSPDF from 'jspdf';

/**
 * Sanitize CSV cell value to prevent CSV injection (CWE-1236)
 * Prepends a single quote to values that start with dangerous characters
 * to ensure they are treated as text by spreadsheet software
 * @param {any} value - The cell value to sanitize (can be any type)
 * @returns {string} - Sanitized value with single quote prefix if needed
 */
const sanitizeCSVCell = (value) => {
  // Handle null, undefined, and empty values
  if (value === null || value === undefined) {
    return '';
  }
  
  // Convert to string - handles all types (numbers, booleans, objects, etc.)
  const stringValue = String(value);
  
  // Handle empty strings after conversion
  if (stringValue.length === 0) {
    return '';
  }
  
  // Check if value starts with dangerous characters that could trigger formula injection
  // Dangerous characters: =, +, -, @, Tab (0x09), Carriage return (0x0D)
  // These characters at the start of a cell can be interpreted as formulas by spreadsheet software
  const dangerousChars = ['=', '+', '-', '@', '\t', '\r'];
  const firstChar = stringValue.charAt(0);
  
  // If value starts with a dangerous character, prepend single quote
  // This forces spreadsheet software to treat the content as text, not a formula
  if (dangerousChars.includes(firstChar)) {
    return `'${stringValue}`;
  }
  
  return stringValue;
};

/**
 * Export table data to PDF
 * @param {Array} data - Array of objects representing table rows
 * @param {Array} columns - Array of column definitions with field and headerName
 * @param {string} filename - Name of the PDF file (without extension)
 */
export const exportToPDF = (data, columns, filename = 'table-export') => {
  try {
    const doc = new JSPDF('l', 'mm', 'a4'); // landscape orientation for better table fit
    
    // Extract column headers and field names
    const tableColumns = columns
      .filter(col => col.field !== 'actions' && col.field !== 'id')
      .map(col => ({
        header: col.headerName || col.field,
        dataKey: col.field
      }));

    // Prepare table data
    const tableData = data.map(row => {
      return tableColumns.map(col => {
        const value = row[col.dataKey];
        // Handle null/undefined values
        if (value === null || value === undefined) return '—';
        // Convert dates if needed
        if (value instanceof Date) {
          return value.toLocaleDateString();
        }
        // Truncate very long text
        const stringValue = String(value);
        return stringValue.length > 50 ? stringValue.substring(0, 50) + '...' : stringValue;
      });
    });

    // Constants for table layout
    const pageWidth = 297; // A4 landscape width in mm
    const pageHeight = 210; // A4 landscape height in mm
    const margin = 10;
    const startY = 20;
    const rowHeight = 8;
    const headerHeight = 10;
    const fontSize = 9;
    const headerFontSize = 10;
    
    // Calculate column widths (equal distribution)
    const availableWidth = pageWidth - (margin * 2);
    const colWidth = availableWidth / tableColumns.length;
    
    let currentY = startY;
    
    // Add title (make it dynamic based on filename)
    doc.setFontSize(16);
    const title = filename.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') + ' Report';
    doc.text(title, margin, 12);
    
    // Add date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const dateText = `Generated on: ${new Date().toLocaleString()}`;
    const dateWidth = doc.getTextWidth(dateText);
    doc.text(dateText, pageWidth - margin - dateWidth, 12);
    doc.setTextColor(0, 0, 0);
    
    // Draw header row
    doc.setFillColor(41, 128, 185);
    doc.rect(margin, currentY, availableWidth, headerHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(headerFontSize);
    doc.setFont(undefined, 'bold');
    
    tableColumns.forEach((col, index) => {
      const x = margin + (index * colWidth);
      const headerText = col.header.length > 20 ? col.header.substring(0, 17) + '...' : col.header;
      doc.text(headerText, x + 2, currentY + 7);
    });
    
    currentY += headerHeight;
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
    
    // Draw data rows
    tableData.forEach((row, rowIndex) => {
      // Check if we need a new page
      if (currentY + rowHeight > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }
      
      // Alternate row color
      if (rowIndex % 2 === 0) {
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, currentY, availableWidth, rowHeight, 'F');
      }
      
      // Draw row data
      doc.setFontSize(fontSize);
      row.forEach((cell, colIndex) => {
        const x = margin + (colIndex * colWidth);
        // Truncate cell text if too long for column width
        const cellText = String(cell);
        const maxChars = Math.floor((colWidth - 4) / 2); // Rough estimate: 2mm per character
        const displayText = cellText.length > maxChars ? cellText.substring(0, maxChars - 3) + '...' : cellText;
        doc.text(displayText, x + 2, currentY + 6);
      });
      
      // Draw row border
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, currentY + rowHeight, margin + availableWidth, currentY + rowHeight);
      
      currentY += rowHeight;
    });
    
    // Draw column borders
    doc.setDrawColor(200, 200, 200);
    for (let i = 0; i <= tableColumns.length; i++) {
      const x = margin + (i * colWidth);
      doc.line(x, startY, x, currentY);
    }

    // Save PDF
    doc.save(`${filename}-${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error('Failed to export PDF. Please try again.');
  }
};

/**
 * Export table data to CSV format
 * @param {Array} data - Array of objects representing table rows
 * @param {Array} columns - Array of column definitions with field and headerName
 * @param {string} filename - Name of the CSV file (without extension)
 */
export const exportToCSV = (data, columns, filename = 'table-export') => {
  try {
    // Extract column headers and field names
    const tableColumns = columns
      .filter(col => col.field !== 'actions' && col.field !== 'id')
      .map(col => ({
        header: col.headerName || col.field,
        dataKey: col.field
      }));

    // Create CSV header row - sanitize headers to prevent CSV injection
    const headers = tableColumns.map(col => {
      const headerValue = col.header || '';
      // Sanitize header to prevent CSV injection
      const sanitizedHeader = sanitizeCSVCell(headerValue);
      return `"${String(sanitizedHeader).replace(/"/g, '""')}"`;
    }).join(',');
    
    // Create CSV data rows - sanitize ALL fields to prevent CSV injection
    const rows = data.map(row => {
      return tableColumns.map(col => {
        const value = row[col.dataKey];
        // Handle null/undefined values
        if (value === null || value === undefined) return '""';
        
        // Convert dates if needed
        if (value instanceof Date) {
          const dateStr = value.toLocaleDateString();
          // Sanitize date string to prevent CSV injection
          const sanitizedDate = sanitizeCSVCell(dateStr);
          return `"${sanitizedDate.replace(/"/g, '""')}"`;
        }
        
        // Handle arrays (like permissions) - sanitize each item
        if (Array.isArray(value)) {
          const arrayStr = value.map(item => {
            if (typeof item === 'object' && item !== null) {
              // Extract string value from object and sanitize
              const objValue = item.name || item.label || JSON.stringify(item);
              return sanitizeCSVCell(objValue);
            }
            // Sanitize each array item
            return sanitizeCSVCell(String(item));
          }).join('; ');
          // Sanitize the final array string to prevent CSV injection
          const sanitizedArray = sanitizeCSVCell(arrayStr);
          return `"${sanitizedArray.replace(/"/g, '""')}"`;
        }
        
        // Handle objects - convert to string and sanitize
        if (typeof value === 'object' && value !== null) {
          const objStr = JSON.stringify(value);
          const sanitizedObj = sanitizeCSVCell(objStr);
          return `"${sanitizedObj.replace(/"/g, '""')}"`;
        }
        
        // Sanitize ALL other values (strings, numbers, etc.) to prevent CSV injection
        const sanitizedValue = sanitizeCSVCell(value);
        const stringValue = String(sanitizedValue).replace(/"/g, '""');
        return `"${stringValue}"`;
      }).join(',');
    });

    // Combine header and rows
    const csvContent = [headers, ...rows].join('\n');
    
    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error('Failed to export CSV. Please try again.');
  }
};

/**
 * Export table data to Excel (CSV format) - alias for CSV
 * @param {Array} data - Array of objects representing table rows
 * @param {Array} columns - Array of column definitions with field and headerName
 * @param {string} filename - Name of the CSV file (without extension)
 */
export const exportToExcel = (data, columns, filename = 'table-export') => {
  // Excel export is same as CSV
  return exportToCSV(data, columns, filename);
};

